import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Loader2, ArrowLeft, CheckCircle, XCircle, FileText, User, Users,
  Building2, DollarSign, MessageSquare, Send, Banknote, TrendingUp, BarChart3
} from "lucide-react";
import { API } from "../data/constants";

const STAGE_META = {
  terms:   { label: "Terms",   text: "text-violet-700", bg: "bg-violet-50" },
  funding: { label: "Funding", text: "text-teal-700",   bg: "bg-teal-50" },
  funded:  { label: "Funded",  text: "text-emerald-700",bg: "bg-emerald-50" },
};
const LOAN_LABELS = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up" };
const NOTE_TYPES = [
  { value: "funding", label: "Funding" },
  { value: "closing", label: "Closing" },
  { value: "disbursement", label: "Disbursement" },
  { value: "general", label: "General" },
];
const EVENT_TYPES = [
  { value: "wire_in", label: "Wire In" },
  { value: "commitment", label: "Commitment" },
  { value: "adjustment", label: "Adjustment" },
  { value: "fee", label: "Fee" },
];
const VALID_TRANSITIONS = { terms: ["funding", "capital_matching"], funding: ["funded", "terms"] };
import { fmtUSD } from "../utils/currency";
function fmt$(v) { return fmtUSD(v); }
function fmtDollars(v) { return fmtUSD(v); }

export default function FundingDetail({ leadId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [transitioning, setTransitioning] = useState(false);
  const [transitionReason, setTransitionReason] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [noteType, setNoteType] = useState("funding");
  const [submittingNote, setSubmittingNote] = useState(false);
  const [eventType, setEventType] = useState("wire_in");
  const [eventAmount, setEventAmount] = useState("");
  const [eventDesc, setEventDesc] = useState("");
  const [submittingEvent, setSubmittingEvent] = useState(false);
  const [fundAmount, setFundAmount] = useState("");
  const [fundNote, setFundNote] = useState("");
  const [submittingFund, setSubmittingFund] = useState(false);
  const [returns, setReturns] = useState(null);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDetail = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/funding/deal/${leadId}`, { headers })
      .then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
    axios.get(`${API}/analytics/deal/${leadId}/returns`, { headers })
      .then(r => setReturns(r.data)).catch(() => setReturns(null));
  }, [leadId]);
  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleTransition = async (toStage) => {
    setTransitioning(true);
    try {
      await axios.post(`${API}/funding/deal/${leadId}/transition`, { to_stage: toStage, reason: transitionReason || null }, { headers });
      setTransitionReason(""); fetchDetail();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
    finally { setTransitioning(false); }
  };
  const handleRecordEvent = async () => {
    const cents = Math.round(parseFloat(eventAmount) * 100);
    if (!cents || cents <= 0) return;
    setSubmittingEvent(true);
    try {
      await axios.post(`${API}/funding/deal/${leadId}/events`, { event_type: eventType, amount_cents: cents, description: eventDesc || null }, { headers });
      setEventAmount(""); setEventDesc(""); fetchDetail();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
    finally { setSubmittingEvent(false); }
  };
  const handleMarkFunded = async () => {
    const cents = Math.round(parseFloat(fundAmount) * 100);
    if (!cents || cents <= 0) return;
    setSubmittingFund(true);
    try {
      await axios.post(`${API}/funding/deal/${leadId}/mark-funded`, { loan_amount_cents: cents, note: fundNote || null }, { headers });
      setFundAmount(""); setFundNote(""); fetchDetail();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
    finally { setSubmittingFund(false); }
  };
  const handleAddNote = async () => {
    if (!noteContent.trim()) return;
    setSubmittingNote(true);
    try {
      await axios.post(`${API}/funding/deal/${leadId}/notes`, { content: noteContent, note_type: noteType }, { headers });
      setNoteContent(""); fetchDetail();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
    finally { setSubmittingNote(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-teal-600" /></div>;
  if (!data) return <div className="text-center py-20 text-sm text-[#888]">Deal not found</div>;

  const { deal, extra, borrower, documents, notes, capital_stack, term_sheets, funded_deal, investor_commitments, opportunity_summary, readiness } = data;
  const stage = deal.stage;
  const stageMeta = STAGE_META[stage] || STAGE_META.terms;
  const transitions = VALID_TRANSITIONS[stage] || [];
  const isFunded = stage === "funded";

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "capital_stack", label: "Capital Stack" },
    { key: "investors", label: `Investors (${investor_commitments?.length || 0})` },
    { key: "documents", label: `Documents (${documents.length})` },
    { key: "notes", label: `Notes (${notes.length})` },
  ];

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8" data-testid="funding-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-[#888] hover:text-[#333] mb-4 transition-colors" data-testid="funding-back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to Pipeline
      </button>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-bold text-[#0A0A0A]">{deal.deal_number}</h1>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${stageMeta.bg} ${stageMeta.text}`} data-testid="funding-stage-badge">{stageMeta.label}</span>
          </div>
          <p className="text-sm text-[#666]">{deal.borrower_name} · {LOAN_LABELS[deal.loan_type] || deal.loan_type} · {deal.property_address || deal.state}</p>
        </div>
        {transitions.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="funding-transition-actions">
            <input type="text" placeholder="Reason" value={transitionReason} onChange={e => setTransitionReason(e.target.value)}
              data-testid="funding-transition-reason" className="px-3 py-2 rounded-lg border border-[#e5e5e5] text-sm w-40 focus:outline-none focus:ring-2 focus:ring-teal-200" />
            {transitions.map(t => (
              <button key={t} onClick={() => handleTransition(t)} disabled={transitioning} data-testid={`funding-transition-${t}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  t === "funded" ? "bg-emerald-600 text-white hover:bg-emerald-700"
                  : t === "funding" ? "bg-teal-600 text-white hover:bg-teal-700"
                  : "bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100"
                } disabled:opacity-50`}>
                {transitioning ? <Loader2 className="h-4 w-4 animate-spin" /> : (STAGE_META[t]?.label || t.replace("_", " "))}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Funding Readiness Panel */}
      <div className={`rounded-xl border p-4 mb-6 ${isFunded ? "bg-emerald-50 border-emerald-200" : readiness.ready_to_fund ? "bg-teal-50 border-teal-200" : "bg-amber-50 border-amber-200"}`} data-testid="funding-readiness-panel">
        <div className="flex items-center gap-2 mb-3">
          <Banknote className={`h-4 w-4 ${isFunded ? "text-emerald-600" : readiness.ready_to_fund ? "text-teal-600" : "text-amber-600"}`} />
          <span className="text-sm font-semibold text-[#0A0A0A]">
            {isFunded ? "Deal Funded" : readiness.ready_to_fund ? "Ready to Fund" : "Funding In Progress"}
          </span>
          <span className="text-xs text-[#888] ml-auto">{capital_stack.pct_funded}% funded</span>
        </div>
        {/* Progress bar */}
        <div className="w-full h-3 rounded-full bg-white/80 mb-3 border border-[#e5e5e5]">
          <motion.div className={`h-full rounded-full ${isFunded || capital_stack.pct_funded >= 100 ? "bg-emerald-500" : "bg-teal-500"}`}
            initial={{ width: 0 }} animate={{ width: `${Math.min(capital_stack.pct_funded, 100)}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            ["in_funding_stage", readiness.in_funding_stage, "In Funding Stage"],
            ["has_term_sheets", readiness.has_term_sheets, `Term Sheets (${term_sheets?.length || 0})`],
            ["has_lenders", readiness.has_lenders, "Lenders Assigned"],
            ["has_funding_events", readiness.has_funding_events, `Events (${capital_stack.event_count})`],
            ["fully_funded", readiness.fully_funded, "Fully Funded"],
          ].map(([key, val, label]) => (
            <div key={key} className="flex items-center gap-1.5 text-xs">
              {val ? <CheckCircle className="h-3.5 w-3.5 text-emerald-500" /> : <XCircle className="h-3.5 w-3.5 text-[#ccc]" />}
              <span className={val ? "text-[#444]" : "text-[#999]"}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Capital Stack Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="rounded-xl border border-[#eee] bg-white p-4 text-center">
          <div className="text-xs text-[#888] uppercase tracking-wider mb-1">Target</div>
          <div className="text-lg font-bold text-[#0A0A0A]">{fmt$(capital_stack.target_cents)}</div>
        </div>
        <div className="rounded-xl border border-[#eee] bg-white p-4 text-center">
          <div className="text-xs text-[#888] uppercase tracking-wider mb-1">Funded</div>
          <div className="text-lg font-bold text-teal-600">{fmt$(capital_stack.funded_cents)}</div>
        </div>
        <div className="rounded-xl border border-[#eee] bg-white p-4 text-center">
          <div className="text-xs text-[#888] uppercase tracking-wider mb-1">Remaining</div>
          <div className="text-lg font-bold text-amber-600">{fmt$(capital_stack.remaining_cents)}</div>
        </div>
      </div>

      {/* Returns Panel — visible for funded deals */}
      {returns && <ReturnsPanel returns={returns} />}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#eee] mb-6">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`funding-tab-${t.key}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key ? "border-teal-600 text-teal-700" : "border-transparent text-[#888] hover:text-[#333]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
          {tab === "overview" && <OverviewTab deal={deal} extra={extra} borrower={borrower} fundedDeal={funded_deal} />}
          {tab === "capital_stack" && (
            <CapitalStackTab stack={capital_stack} isFunded={isFunded}
              eventType={eventType} setEventType={setEventType} eventAmount={eventAmount} setEventAmount={setEventAmount}
              eventDesc={eventDesc} setEventDesc={setEventDesc} onRecordEvent={handleRecordEvent} submittingEvent={submittingEvent}
              fundAmount={fundAmount} setFundAmount={setFundAmount} fundNote={fundNote} setFundNote={setFundNote}
              onMarkFunded={handleMarkFunded} submittingFund={submittingFund} stage={stage} />
          )}
          {tab === "investors" && <InvestorCommitmentsTab commitments={investor_commitments} summary={opportunity_summary} />}
          {tab === "documents" && <DocumentsTab documents={documents} />}
          {tab === "notes" && (
            <NotesTab notes={notes} noteContent={noteContent} setNoteContent={setNoteContent}
              noteType={noteType} setNoteType={setNoteType} onSubmit={handleAddNote} submitting={submittingNote} />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function ReturnsPanel({ returns }) {
  const m = returns?.metrics;
  if (!m) return null;
  const irrLabel = m.irr_status === "calculated"
    ? `${m.irr_annualized}%`
    : m.irr_status === "no_convergence" ? "N/C" : "—";
  const irrSub = m.irr_status === "calculated"
    ? "Annualized" : m.irr_status === "insufficient_data" ? "Insufficient data" : "No convergence";
  const moicLabel = m.moic != null ? `${m.moic.toFixed(2)}x` : "—";
  const moicColor = !m.moic ? "text-[#888]" : m.moic >= 2 ? "text-emerald-600" : m.moic >= 1.5 ? "text-teal-600" : m.moic >= 1 ? "text-[#0A0A0A]" : "text-red-500";
  const irrColor = m.irr_status !== "calculated" ? "text-[#888]" : m.irr_annualized >= 15 ? "text-emerald-600" : m.irr_annualized >= 8 ? "text-teal-600" : "text-[#0A0A0A]";

  return (
    <div className="rounded-xl border border-[#eee] bg-white p-4 mb-6" data-testid="returns-panel">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="h-4 w-4 text-teal-500" />
        <span className="text-sm font-semibold text-[#0A0A0A]">Returns & Performance</span>
        {m.days_since_funding != null && (
          <span className="text-[10px] text-[#aaa] ml-auto">{m.days_since_funding} days since funding</span>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="text-center">
          <div className="text-xs text-[#888] uppercase tracking-wider mb-1">MOIC</div>
          <div className={`text-xl font-bold ${moicColor}`} data-testid="returns-moic">{moicLabel}</div>
          <div className="text-[10px] text-[#aaa] capitalize">{(m.moic_status || "—").replace(/_/g, " ")}</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-[#888] uppercase tracking-wider mb-1">IRR</div>
          <div className={`text-xl font-bold ${irrColor}`} data-testid="returns-irr">{irrLabel}</div>
          <div className="text-[10px] text-[#aaa]">{irrSub}</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-[#888] uppercase tracking-wider mb-1">Invested</div>
          <div className="text-xl font-bold text-[#0A0A0A]" data-testid="returns-invested">{fmt$(m.total_invested_cents)}</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-[#888] uppercase tracking-wider mb-1">Returned</div>
          <div className="text-xl font-bold text-teal-600" data-testid="returns-returned">{fmt$(m.total_returned_cents)}</div>
        </div>
      </div>
      {returns.cash_flows && returns.cash_flows.length > 0 && (
        <div className="mt-4 pt-3 border-t border-[#f0f0f0]">
          <div className="text-xs text-[#888] uppercase tracking-wider mb-2">Cash Flow History ({returns.cash_flows.length})</div>
          <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="returns-cashflows">
            {returns.cash_flows.map((cf, i) => (
              <div key={i} className="flex items-center gap-3 text-xs py-1" data-testid={`returns-cf-${i}`}>
                <span className="text-[#aaa] w-20 shrink-0">{cf.date}</span>
                <span className={`font-semibold w-24 text-right shrink-0 ${cf.amount_cents < 0 ? "text-red-500" : "text-emerald-600"}`}>
                  {cf.amount_cents < 0 ? "-" : "+"}{fmt$(Math.abs(cf.amount_cents))}
                </span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${cf.type === "investment" ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"}`}>
                  {cf.type}
                </span>
                <span className="text-[#888] truncate">{cf.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function OverviewTab({ deal, extra, borrower, fundedDeal }) {
  const sections = [
    { title: "Deal Summary", icon: DollarSign, items: [
      ["Loan Type", LOAN_LABELS[deal.loan_type] || deal.loan_type],
      ["Purchase Price", fmtDollars(deal.purchase_price)], ["Rehab Budget", fmtDollars(deal.rehab_budget)],
      ["ARV", fmtDollars(deal.arv)], ["Requested Loan", fmtDollars(deal.requested_loan_amount)],
      ["LTV", deal.purchase_price ? `${((deal.requested_loan_amount / deal.purchase_price) * 100).toFixed(1)}%` : "—"],
      ["Exit Strategy", extra?.exit_strategy || "—"],
    ]},
    { title: "Property", icon: Building2, items: [
      ["Address", deal.property_address || "—"],
      ["Type", extra?.property_type || deal.property_type || "—"],
      ["Beds / Baths", `${extra?.bedrooms || "—"} / ${extra?.bathrooms || "—"}`],
      ["Sqft", extra?.sqft ? `${Number(extra.sqft).toLocaleString()} sqft` : "—"],
    ]},
    { title: "Borrower", icon: User, items: [
      ["Name", borrower?.full_name || deal.borrower_name || "—"],
      ["Email", borrower?.email || extra?.email || "—"],
      ["Phone", borrower?.phone || extra?.phone || "—"],
      ["Entity", borrower?.entity_name || extra?.entity_name || "—"],
    ]},
  ];
  return (
    <div className="space-y-4">
      {fundedDeal && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="funded-deal-record">
          <div className="flex items-center gap-2 mb-2"><CheckCircle className="h-4 w-4 text-emerald-600" /><span className="text-sm font-semibold text-emerald-700">Funded Deal Record</span></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div><span className="text-[#888]">Loan Amount: </span><span className="font-medium">{fmt$(fundedDeal.loan_amount)}</span></div>
            <div><span className="text-[#888]">Status: </span><span className="font-medium text-emerald-600">{fundedDeal.status}</span></div>
            <div><span className="text-[#888]">Funded At: </span><span className="font-medium">{fundedDeal.funded_at?.slice(0, 10)}</span></div>
            <div><span className="text-[#888]">ID: </span><span className="font-medium">{fundedDeal.funded_deal_id?.slice(0, 8)}...</span></div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {sections.map(s => (
          <div key={s.title} className="rounded-xl border border-[#eee] bg-white p-4">
            <div className="flex items-center gap-2 mb-3"><s.icon className="h-4 w-4 text-teal-500" /><span className="text-sm font-semibold text-[#0A0A0A]">{s.title}</span></div>
            <dl className="space-y-2">{s.items.map(([l, v]) => (<div key={l} className="flex justify-between text-xs"><dt className="text-[#888]">{l}</dt><dd className="text-[#333] font-medium text-right max-w-[60%] truncate">{v}</dd></div>))}</dl>
          </div>
        ))}
      </div>
    </div>
  );
}

function CapitalStackTab({ stack, isFunded, eventType, setEventType, eventAmount, setEventAmount, eventDesc, setEventDesc, onRecordEvent, submittingEvent, fundAmount, setFundAmount, fundNote, setFundNote, onMarkFunded, submittingFund, stage }) {
  return (
    <div className="space-y-4">
      {/* Record funding event */}
      {!isFunded && (
        <div className="rounded-xl border border-[#eee] bg-white p-4" data-testid="record-event-form">
          <div className="flex items-center gap-2 mb-3"><TrendingUp className="h-4 w-4 text-teal-500" /><span className="text-sm font-semibold text-[#0A0A0A]">Record Funding Event</span></div>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {EVENT_TYPES.map(t => (
              <button key={t.value} onClick={() => setEventType(t.value)} data-testid={`event-type-${t.value}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${eventType === t.value ? "bg-teal-100 text-teal-700 border border-teal-200" : "bg-[#f8f8f8] text-[#888] border border-transparent hover:border-[#ddd]"}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#888]">$</span>
              <input type="number" value={eventAmount} onChange={e => setEventAmount(e.target.value)} placeholder="Amount"
                data-testid="event-amount-input" className="w-full pl-7 pr-4 py-2.5 rounded-lg border border-[#e5e5e5] text-sm focus:outline-none focus:ring-2 focus:ring-teal-200" />
            </div>
            <input type="text" value={eventDesc} onChange={e => setEventDesc(e.target.value)} placeholder="Description (optional)"
              data-testid="event-desc-input" className="flex-1 px-3 py-2.5 rounded-lg border border-[#e5e5e5] text-sm focus:outline-none focus:ring-2 focus:ring-teal-200" />
          </div>
          <button onClick={onRecordEvent} disabled={submittingEvent || !eventAmount} data-testid="record-event-btn"
            className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50 transition-all flex items-center gap-2">
            {submittingEvent ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />} Record Event
          </button>
        </div>
      )}

      {/* Mark Funded */}
      {(stage === "funding" || stage === "terms") && !isFunded && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4" data-testid="mark-funded-form">
          <div className="flex items-center gap-2 mb-3"><Banknote className="h-4 w-4 text-emerald-600" /><span className="text-sm font-semibold text-[#0A0A0A]">Mark Deal as Funded</span></div>
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#888]">$</span>
              <input type="number" value={fundAmount} onChange={e => setFundAmount(e.target.value)} placeholder="Final loan amount"
                data-testid="fund-amount-input" className="w-full pl-7 pr-4 py-2.5 rounded-lg border border-emerald-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
            </div>
            <input type="text" value={fundNote} onChange={e => setFundNote(e.target.value)} placeholder="Closing note"
              data-testid="fund-note-input" className="flex-1 px-3 py-2.5 rounded-lg border border-emerald-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
          </div>
          <button onClick={onMarkFunded} disabled={submittingFund || !fundAmount} data-testid="mark-funded-btn"
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-all flex items-center gap-2">
            {submittingFund ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />} Mark Funded
          </button>
        </div>
      )}

      {/* Ledger events */}
      <div className="rounded-xl border border-[#eee] bg-white p-4">
        <div className="text-xs uppercase tracking-wider text-[#888] mb-3">Funding Events ({stack.event_count})</div>
        {stack.events.length === 0 ? <p className="text-sm text-[#888] text-center py-4">No events recorded</p> : (
          <div className="space-y-2" data-testid="funding-events-list">
            {stack.events.map((ev, i) => (
              <div key={ev.event_id || i} className="flex items-center gap-3 p-3 rounded-lg border border-[#f0f0f0]" data-testid={`funding-event-${i}`}>
                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center shrink-0">
                  <DollarSign className="h-4 w-4 text-teal-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#0A0A0A]">{ev.description || ev.event_type}</div>
                  <div className="text-xs text-[#888]">{ev.event_type} · {ev.timestamp?.slice(0, 16).replace("T", " ")}</div>
                </div>
                <div className="text-sm font-semibold text-teal-600 shrink-0">{fmt$(ev.amount_cents)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Investor Commitments Tab ─────────────────────────── */
const COMMIT_STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700",
  confirmed: "bg-blue-100 text-blue-700",
  funded: "bg-emerald-100 text-emerald-700",
  withdrawn: "bg-red-100 text-red-600",
};
function InvestorCommitmentsTab({ commitments, summary }) {
  if (!commitments?.length) return (
    <div className="text-center py-12">
      <DollarSign className="h-8 w-8 text-[#ccc] mx-auto mb-2" />
      <p className="text-sm text-[#888]">No investor commitments yet</p>
    </div>
  );
  const active = commitments.filter(c => c.status !== "withdrawn");
  const totalRaised = active.reduce((s, c) => s + (c.amount || 0), 0);
  return (
    <div className="space-y-4" data-testid="investor-commitments-tab">
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-xl border border-[#eee] bg-white p-4 text-center">
            <div className="text-lg font-bold text-[#0A0A0A]">${totalRaised.toLocaleString()}</div>
            <div className="text-[10px] uppercase text-[#888] tracking-wider">Raised</div>
          </div>
          <div className="rounded-xl border border-[#eee] bg-white p-4 text-center">
            <div className="text-lg font-bold text-[#0A0A0A]">${(summary.target_raise || 0).toLocaleString()}</div>
            <div className="text-[10px] uppercase text-[#888] tracking-wider">Target</div>
          </div>
          <div className="rounded-xl border border-[#eee] bg-white p-4 text-center">
            <div className="text-lg font-bold text-[#0A0A0A]">{summary.investor_count || 0}</div>
            <div className="text-[10px] uppercase text-[#888] tracking-wider">Investors</div>
          </div>
        </div>
      )}
      <div className="rounded-xl border border-[#eee] bg-white p-4">
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-4 w-4 text-teal-500" />
          <span className="text-sm font-semibold text-[#0A0A0A]">Commitments ({commitments.length})</span>
        </div>
        <div className="space-y-2">
          {commitments.map((c, i) => (
            <div key={c.commitment_id || i} className="flex items-center justify-between p-3 rounded-lg border border-[#f0f0f0]" data-testid={`investor-commitment-${i}`}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                  <User className="h-4 w-4 text-teal-600" />
                </div>
                <div>
                  <div className="text-sm font-medium text-[#0A0A0A]">{c.investor_name || "Investor"}</div>
                  <div className="text-[10px] text-[#aaa]">{c.created_at?.slice(0, 10)}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-[#0A0A0A]">${(c.amount || 0).toLocaleString()}</div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${COMMIT_STATUS_COLORS[c.status] || "bg-gray-100 text-gray-600"}`}>
                  {(c.status || "").toUpperCase()}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DocumentsTab({ documents }) {
  if (!documents.length) return <div className="text-center py-12"><FileText className="h-8 w-8 text-[#ccc] mx-auto mb-2" /><p className="text-sm text-[#888]">No documents</p></div>;
  return (
    <div className="space-y-2" data-testid="funding-documents-list">
      {documents.map((doc, i) => (
        <div key={doc.doc_id || i} className="flex items-center gap-3 rounded-xl border border-[#eee] bg-white p-3">
          <FileText className="h-5 w-5 text-teal-400 shrink-0" />
          <div className="min-w-0 flex-1"><div className="text-sm text-[#0A0A0A] truncate">{doc.filename}</div><div className="text-xs text-[#888]">{doc.category_label || doc.category}</div></div>
          <span className="text-[10px] text-[#aaa]">{doc.created_at?.slice(0, 10)}</span>
        </div>
      ))}
    </div>
  );
}

function NotesTab({ notes, noteContent, setNoteContent, noteType, setNoteType, onSubmit, submitting }) {
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-[#eee] bg-white p-4" data-testid="funding-add-note-form">
        <div className="flex items-center gap-2 mb-3"><MessageSquare className="h-4 w-4 text-teal-500" /><span className="text-sm font-semibold text-[#0A0A0A]">Add Note</span></div>
        <div className="flex gap-2 mb-3">
          {NOTE_TYPES.map(t => (
            <button key={t.value} onClick={() => setNoteType(t.value)} data-testid={`funding-note-type-${t.value}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${noteType === t.value ? "bg-teal-100 text-teal-700 border border-teal-200" : "bg-[#f8f8f8] text-[#888] border border-transparent hover:border-[#ddd]"}`}>{t.label}</button>
          ))}
        </div>
        <textarea value={noteContent} onChange={e => setNoteContent(e.target.value)} placeholder="Write your note..." data-testid="funding-note-content"
          className="w-full p-3 rounded-lg border border-[#e5e5e5] text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-teal-200" />
        <button onClick={onSubmit} disabled={submitting || !noteContent.trim()} data-testid="funding-submit-note-btn"
          className="mt-2 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50 transition-all flex items-center gap-2">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Add Note
        </button>
      </div>
      <div className="space-y-2">
        {notes.map((n, i) => (
          <div key={n.note_id || i} className="rounded-xl border border-[#eee] bg-white p-4" data-testid={`funding-note-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-[#444]">{n.author_name}</span>
              <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${
                n.note_type === "funding" ? "bg-teal-50 text-teal-600" : n.note_type === "closing" ? "bg-emerald-50 text-emerald-600" : "bg-[#f5f5f5] text-[#888]"
              }`}>{n.note_type}</span>
              <span className="text-[10px] text-[#aaa] ml-auto">{n.created_at?.slice(0, 16).replace("T", " ")}</span>
            </div>
            <p className="text-sm text-[#333] whitespace-pre-wrap">{n.content}</p>
          </div>
        ))}
        {!notes.length && <p className="text-sm text-[#888] text-center py-6">No notes yet</p>}
      </div>
    </div>
  );
}
